import { WIDGET_OPENING_TOP_PX } from '~/viewportGrid';

/** Room left above a dock so it clears the views' title bars, in CSS pixels. */
const TOP_CLEARANCE_PX = WIDGET_OPENING_TOP_PX;
/**
 * Least gap between a docked dock and the host's edges, in CSS pixels: as
 * far as a menu opens from its button (`MuiMenu` paper's margin).
 */
export const DOCKED_INSET_PX = 4;
/** Least height a docked dock keeps for its options, in CSS pixels. */
const MIN_HEIGHT_PX = 160;
/** How far a dock's edge sits beyond its button's, in CSS pixels. */
const BUTTON_OVERHANG_PX = 4;
/** Least room a dock growing up keeps above its bottom edge, in CSS pixels. */
const MIN_ROOM_ABOVE_PX = 150;

/**
 * Where a docked dock sits in its host: `top` set when it grows down from
 * there, `bottom` set when it grows up from there, both in CSS pixels from
 * that edge of the host, and the height its options may take.
 */
export type DockPlacement =
    | { top: number; bottom?: undefined; maxHeight: number }
    | { top?: undefined; bottom: number; maxHeight: number };

/**
 * Places a dock level with the button that opened it. A button in the upper
 * half of the host gets a dock that starts at the button and grows down; one
 * in the lower half gets a dock that ends at the button and grows up, so the
 * options of a low button keep the host's height rather than the room under
 * the button. Without a button the dock starts at the top. Rects are client
 * rects, in CSS pixels.
 */
export function dockPlacement(
    host: DOMRectReadOnly,
    button: DOMRectReadOnly | null
): DockPlacement {
    const hostHeight = host.height;
    const buttonTop = button ? button.top - host.top - BUTTON_OVERHANG_PX : 0;
    if (!button || buttonTop <= hostHeight / 2) {
        const top = Math.max(TOP_CLEARANCE_PX, buttonTop);
        return {
            top,
            maxHeight: Math.max(
                MIN_HEIGHT_PX,
                hostHeight - top - 2 * DOCKED_INSET_PX
            ),
        };
    }
    const buttonBottom = button.bottom - host.top + BUTTON_OVERHANG_PX;
    const bottom = Math.max(
        DOCKED_INSET_PX,
        Math.min(hostHeight - buttonBottom, hostHeight - MIN_ROOM_ABOVE_PX)
    );
    return {
        bottom,
        maxHeight: Math.max(
            MIN_HEIGHT_PX,
            hostHeight - bottom - TOP_CLEARANCE_PX
        ),
    };
}
